const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const componentToHex = (c: number) => {
  const hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
};

const rgbToHex = (r: number, g: number, b: number) => {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
};

const getTint = (color: number, factor: number) => {
  return Math.round(color + (255 - color) * factor);
};

const getShade = (color: number, factor: number) => {
  return Math.round(color * factor);
};

/**
 * Mix a hex color toward white by `amount` (0–1).
 * Useful for improving primary contrast on dark backgrounds.
 */
export const lightenHex = (hex: string, amount = 0.2): string => {
  const color = hexToRgb(hex);
  if (!color) {
    return hex;
  }

  const clamped = Math.min(1, Math.max(0, amount));

  return rgbToHex(
    getTint(color.r, clamped),
    getTint(color.g, clamped),
    getTint(color.b, clamped),
  );
};

export const generateShadesAndTints = (hex: string) => {
  const color = hexToRgb(hex);

  if (!color) {
    throw new Error("Invalid hex color provided");
  }

  const keysMap = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0];
  interface ColorSchema {
    [key: string]: string | undefined;
    10?: string;
    20?: string;
    30?: string;
    40?: string;
    50?: string;
    60?: string;
    70?: string;
    80?: string;
    90?: string;
  }
  const colors: { tints: ColorSchema; shades: ColorSchema } = {
    tints: {},
    shades: {},
  };
  let j = 0;

  //Loop through rgb values multiplying them to get shades or tints
  for (let i = 0; i <= 1; i += 0.1) {
    j = Math.round(j * 10) / 10;

    const tint = {
      r: getTint(color.r, j),
      g: getTint(color.g, j),
      b: getTint(color.b, j),
    };
    colors.tints[keysMap[Math.round(j * 10)].toString() as keyof ColorSchema] = rgbToHex(tint.r, tint.g, tint.b);

    const shade = {
      r: getShade(color.r, j),
      g: getShade(color.g, j),
      b: getShade(color.b, j),
    };
    colors.shades[keysMap[Math.round(j * 10)].toString() as keyof ColorSchema] = rgbToHex(shade.r, shade.g, shade.b);
    j += 0.1;
  }

  return colors;
};

export const isLight = (color: string): boolean => {
  let r: number, g: number, b: number, hsp: number;
  color = color.replace('#', '');

  // Convert short hex to full hex
  if (color.length === 3) {
    color = color
      .split('')
      .map((char) => char + char)
      .join('');
  }

  r = parseInt(color.substring(0, 2), 16);
  g = parseInt(color.substring(2, 4), 16);
  b = parseInt(color.substring(4, 6), 16);

  hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  return hsp > 200;
};
